'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function YuzDolgusuSonrasiDikkatEdilmesiGerekenler() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/yuz-dolgusu-sonrasi-dikkat-edilmesi-gerekenler-bilimsel-ve-klinik-rehber';
  const pageTitle = 'Yüz Dolgusu Sonrası Dikkat Edilmesi Gerekenler: Bilimsel ve Klinik Rehber';
  const pageDescription = 'Yüz dolgusu sonrasında nelere dikkat edilmelidir? Masaj, uyku, alkol, egzersiz, uçuş ve beslenme dâhil tüm kuralların bilimsel açıklaması.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Yüz Dolgusu Sonrası Dikkat Edilmesi Gerekenler', href: '/tr/blog/yuz-dolgusu-sonrasi-dikkat-edilmesi-gerekenler-bilimsel-ve-klinik-rehber' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Yüz dolgusu sonrası bakım ve dikkat edilmesi gerekenler"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-cyan-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Bakım Rehberi
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Yüz Dolgusu Sonrası Dikkat Edilmesi Gerekenler: Bilimsel ve Klinik Rehber
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>9 Mart 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>12 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Yüz dolgusu, cildin altına enjekte edilen hyaluronik asit (HA) temelli bir dolgu maddesiyle hacim, canlılık ve genç görünüm kazandıran en güvenli medikal estetik işlemlerden biridir.
                Ancak sonuçların doğallığı ve kalıcılığı yalnızca kullanılan ürünle değil, uygulama sonrası gösterilen özenle doğrudan ilişkilidir.
              </p>

              <div className="bg-cyan-50 border-l-4 border-cyan-400 p-6 mb-6">
                <p className="text-cyan-800 italic text-lg">
                  "Dolgu sonrası 48 saat, güzelliğin koruma dönemidir."
                </p>
                <p className="text-cyan-800 font-medium mt-2">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣ İlk 48 Saat: En Kritik Dönem
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dolgu işlemi sonrası cilt altında mikrotravmalar ve geçici ödem oluşur.
                Bu nedenle ilk 48 saat boyunca yapılması gerekenler oldukça hassastır.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Bu sürede kaçınılması gerekenler:
              </p>

              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-red-800">
                  <li>Alkol ve sigara 🚫</li>
                  <li>Aşırı mimik hareketleri</li>
                  <li>Masaj ve dokunma</li>
                  <li>Sauna, hamam, sıcak duş</li>
                  <li>Ağır egzersiz</li>
                  <li>Uçak seyahati</li>
                </ul>
              </div>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Serbest olanlar:
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-green-800">
                  <li>Soğuk kompres (15 dk aralıklarla)</li>
                  <li>Bol su tüketimi</li>
                  <li>Hafif yürüyüş</li>
                </ul>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  📍 Bu süreçte dolgu dokuyla bütünleşir; dolaşım bozulursa asimetri ve şişlik riski artar.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2️⃣ Uyku Pozisyonu
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-cyan-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Süre</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Pozisyon</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">İlk 2 gece</td>
                      <td className="border border-gray-300 px-4 py-3">Sırtüstü, baş yüksek</td>
                      <td className="border border-gray-300 px-4 py-3">Basıncı azaltır, dolgu kaymasını önler</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">3–7 gün</td>
                      <td className="border border-gray-300 px-4 py-3">Yan pozisyon uygun ama yüz teması az olmalı</td>
                      <td className="border border-gray-300 px-4 py-3">Cilt baskısı şişlik yapabilir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 mb-6">
                <p className="text-indigo-800 font-medium">
                  💬 Yan yatış alışkanlığı olanlarda dolgu asimetrisi gözlenebilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3️⃣ Mimik ve Kas Hareketleri
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dolgu, kas ve cilt arasındaki mikro boşluklara yerleşir.
                İlk 24 saatte yoğun gülme, çiğneme veya konuşma kasların bu dengeyi bozmasına neden olabilir.
                Bu yüzden işlem günü:
              </p>

              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-yellow-800">
                  <li>Sert gıdaları çiğnemekten kaçının.</li>
                  <li>Aşırı gülme ve konuşma süresini azaltın.</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4️⃣ Sıcaklık ve Basınç
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Yüksek sıcaklık damar genişlemesi yapar ve dolgunun dağılımını etkiler.
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-red-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Etken</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Sonuç</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sauna / Hamam</td>
                      <td className="border border-gray-300 px-4 py-3">Şişlik ve ödem</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Güneşlenme / Solaryum</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu bozulması ve renk değişimi</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sıcak duş</td>
                      <td className="border border-gray-300 px-4 py-3">Damar genişlemesi → morarma</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                İlk 7 gün bu etkenlerden kaçınılmalıdır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5️⃣ Egzersiz ve Spor
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Spor sırasında kalp atım hızı ve kan basıncı arttığı için dolgu baskı altında kalır.
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-green-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Aktivite</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Ne Zaman Yapılabilir</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hafif yürüyüş</td>
                      <td className="border border-gray-300 px-4 py-3">2. günden itibaren</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Orta düzey spor</td>
                      <td className="border border-gray-300 px-4 py-3">5. günden itibaren</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Ağırlık / kardiyo</td>
                      <td className="border border-gray-300 px-4 py-3">7. günden sonra</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6️⃣ Beslenme ve Takviyeler
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-green-50 rounded-xl p-6">
                  <h4 className="font-semibold text-green-900 mb-4">Önerilen</h4>
                  <ul className="list-disc list-inside space-y-2 text-green-800">
                    <li>Bol su (2–2,5 L/gün)</li>
                    <li>C vitamini / antioksidan</li>
                    <li>Ananas ve kivi (doğal antiödem)</li>
                  </ul>
                </div>
                <div className="bg-red-50 rounded-xl p-6">
                  <h4 className="font-semibold text-red-900 mb-4">Kaçınılması Gereken</h4>
                  <ul className="list-disc list-inside space-y-2 text-red-800">
                    <li>Aşırı tuzlu ve baharatlı gıdalar</li>
                    <li>Alkol ve kafein fazlası</li>
                    <li>Kan sulandırıcı ilaçlar (aspirin vb.)</li>
                  </ul>
                </div>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu şekilde dolaşım dengeye gelir ve dolgu doğal bir görünüm alır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7️⃣ Masaj ve Dokunma
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                İlk 3 gün boyunca yüze masaj veya elle baskı yapmak yasaktır.
                Dolgu henüz dokuda sabit değildir.
                Yanlış baskı, mikro kaymalara ve asimetrilere neden olabilir.
                Doktor önerisi olmaksızın hiçbir masaj ya da "buz roller" kullanılmamalıdır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8️⃣ Makyaj ve Cilt Bakımı
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Enjeksiyon bölgesinde mikro delikler vardır.
                Bu bölgeler kapanmadan makyaj yapmak enfeksiyon riskini artırır.
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Ürün</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Uygulama Zamanı</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hafif nemlendirici</td>
                      <td className="border border-gray-300 px-4 py-3">12 saat sonra</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Fondöten / pudra</td>
                      <td className="border border-gray-300 px-4 py-3">24 saat sonra</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Peeling / asitleme</td>
                      <td className="border border-gray-300 px-4 py-3">7 günden sonra</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                9️⃣ Morarma ve Şişlik Yönetimi
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-yellow-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Durum</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Çözüm</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Morarma</td>
                      <td className="border border-gray-300 px-4 py-3">Soğuk kompres, arnika krem ile 3 günde geçer</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Şişlik</td>
                      <td className="border border-gray-300 px-4 py-3">Baş yüksek uyuma, tuzdan kaçınma</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Ağrı</td>
                      <td className="border border-gray-300 px-4 py-3">Parasetamol kullanılabilir (aspirin yasak)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                🔟 Takip ve Kontrol
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dolgunun tam olarak yerleşmesi 10–14 gün sürer.
                Bu süre sonunda kontrol randevusu şarttır.
                Eğer asimetrik görünüm veya sertlik hissediliyorsa, doktor tarafından gerekirse mikro düzeltme enjeksiyonu yapılır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣1️⃣ Uzman Görüşü
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Yüz dolgu uygulaması sonrası en önemli süreç ilk 48 saattir.
                  Bu dönemde dikkatsiz davranmak sonucu değil, yatırımı kaybettirir."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣2️⃣ Etik ve Klinik Yaklaşım
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Estetik bir işlem sadece görünümü değil, özgüveni de etkiler.
                Bu nedenle her hasta işlem sonrası dönem hakkında doğru bilgilendirilmelidir.
              </p>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-4 mb-6">
                <p className="text-purple-800 font-medium">
                  💬 "Güzellik, bilinç ve sabırla başlar."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣3️⃣ Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Dolgu sonrası ne zaman makyaj yapabilirim?</h3>
                  <p className="text-gray-600">24 saat sonra.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Masaj yapmalı mıyım?</h3>
                  <p className="text-gray-600">Hayır, yalnızca doktor önerirse.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Ne zaman spor yapabilirim?</h3>
                  <p className="text-gray-600">Hafif yürüyüş 2. gün, yoğun spor 7. gün.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Morarma ne kadar sürer?</h3>
                  <p className="text-gray-600">Genellikle 3–5 gün.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Ne zaman uçak yolculuğu yapabilirim?</h3>
                  <p className="text-gray-600">En az 48 saat, tercihen 72 saat sonra.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Dolgu ne zaman tam yerleşir?</h3>
                  <p className="text-gray-600">10-14 gün içinde tam olarak yerleşir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣4️⃣ Sonuç ve Klinik Öneri
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Yüz dolgu uygulaması basit bir enjeksiyon değil, doku mimarisine müdahaledir.
                İlk 48–72 saatte doğru davranmak, aylarca kalıcı ve doğal sonuçlar sağlar.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-6">
                <p className="text-red-800 font-medium">
                  🩺 Dr. Barış Çin öneriyor:
                </p>
                <p className="text-red-800 mt-2">
                  "Dolgunun kendisi değil, bakımı kalıcılığı belirler.
                  İlk hafta disiplinli davran, yıllarca doğal kal."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>ISAPS (2024) – "Dermal Filler Aftercare Protocol"</li>
                  <li>Teoxane (2023) – "Hydration and HA Integration in Tissues"</li>
                  <li>Harvard Health (2024) – "Post-Injection Skin Care and Inflammation"</li>
                  <li>TETD (2024) – "Dolgu Sonrası Hasta Bilgilendirme Rehberi"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#yüzdolgu</span>
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#bakım</span>
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#DrBarışÇin</span>
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#klinikrehber</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-cyan-600 to-blue-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Yüz Dolgusu Uygulaması ve Bakım Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan yüz dolgusu uygulaması ve sonrası bakım hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Yüz dolgusu uygulaması ve sonrası bakım hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-cyan-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/yuz-dolgusu-sonrasi-alkol-aliminin-sonuclari"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Yüz Dolgusu Sonrası Alkol Alımının Sonuçları</h4>
              <p className="text-gray-600 text-sm">Yüz dolgu uygulamasından sonra alkol içmek neden sakıncalıdır? Morarma, ödem ve dolgu yer değişimi.</p>
            </a>
            <a
              href="/tr/blog/yuz-dolgusu-sonrasi-ucak-seyahati-basinc-degisimi-odem-riski-ve-uzman-onerileri"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Yüz Dolgusu Sonrası Uçak Seyahati</h4>
              <p className="text-gray-600 text-sm">Yüz dolgu işleminden hemen sonra uçağa binmek doğru mu? Basınç, dolaşım ve ödem üzerindeki etkiler.</p>
            </a>
            <a
              href="/tr/blog/yuz-dolgusunun-alerji-riski-nadir-ama-bilinmesi-gereken-gercekler"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Yüz Dolgusunun Alerji Riski</h4>
              <p className="text-gray-600 text-sm">Yüz dolgu işlemleri alerji yapar mı? Belirtiler, risk grupları ve doğru ürün seçimi.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-cyan-600 text-white rounded-full hover:bg-cyan-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
